import React, { useEffect, useMemo, useState } from "react";
import { Box, Container, Typography, CircularProgress, Alert, Grid, Paper, Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { api } from "../context/AuthContext";
import CandidateTable from "../components/CandidateTable";
import { viewResume } from "../services/fileService";
import API_BASE_URL from "../Config/config";

const BACKEND_URL = API_BASE_URL;

function SummaryCard({ title, value, subtext, icon, color, onClick, selected = false }) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: 0, // was 4
        border: "1px solid #1E3A8A",
        minHeight: 170,
        height: "100%",
        boxShadow: "0 8px 20px rgba(30,58,138,0.12)",
        bgcolor: "#fff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        cursor: onClick ? "pointer" : "default",
        border: selected ? "2px solid #1E3A8A" : "1px solid #1E3A8A",
        transform: selected ? "translateY(-2px)" : "none",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: "rgba(0,0,0,0.04)",
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            lineHeight: 1.25,
            pt: 0.6,
            whiteSpace: "normal",
            wordBreak: "normal",
            overflowWrap: "break-word",
          }}
        >
          {title}
        </Typography>
      </Box>

      <Box sx={{ mt: "auto", pt: 2 }}>
        <Typography variant="h3" fontWeight={800} sx={{ lineHeight: 1 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {subtext}
        </Typography>
      </Box>
    </Paper>
  );
}

export default function CandidateScreening() {
  const { jobId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [activeFilter, setActiveFilter] = useState("ALL");

  useEffect(() => {
    let mounted = true;

    async function fetchAll() {
      setLoading(true);
      setError("");

      try {
        const [jobRes, candRes] = await Promise.all([
          api.get(`/jobs/${jobId}`),
          api.get(`/jobs/${jobId}/candidates`),
        ]);

        if (!mounted) return;
        setJob(jobRes.data || null);
        setCandidates(candRes.data?.candidates || []);
      } catch (e) {
        console.error(e);
        if (!mounted) return;
        setError(e?.response?.data?.message || "Failed to load candidates");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    if (jobId) fetchAll();
    return () => {
      mounted = false;
    };
  }, [jobId]);

  const header = useMemo(() => {
    if (!job) return `Candidate Screening`;
    return `Candidate Screening - ${job.title || "Job"}`;
  }, [job]);

  const subheader = useMemo(() => {
    if (!job) return "";
    const company = job.company_name || job.companyName;
    const location = job.location;
    return [company, location].filter(Boolean).join(" • ");
  }, [job]);

  const stats = useMemo(() => {
    const total = candidates.length;
    
    const shortlisted = candidates.filter((c) => {
      const rec = (c.recommendation || "").toUpperCase().replace(/_/g, "");
      return rec === "SHORTLIST" || rec === "STRONGSHORTLIST";
    }).length;

    const review = candidates.filter((c) => {
      const rec = (c.recommendation || "").toUpperCase().replace(/_/g, "");
      return rec === "REVIEW";
    }).length;

    const rejected = candidates.filter((c) => {
      const rec = (c.recommendation || "").toUpperCase().replace(/_/g, "");
      return rec === "REJECT";
    }).length;

    const getPct = (val) => (total > 0 ? ((val / total) * 100).toFixed(1) : "0.0");

    return {
      total,
      shortlisted: { count: shortlisted, pct: getPct(shortlisted) },
      review: { count: review, pct: getPct(review) },
      rejected: { count: rejected, pct: getPct(rejected) },
    };
  }, [candidates]);

  const filteredCandidates = useMemo(() => {
    if (activeFilter === "ALL") return candidates;

    return candidates.filter((c) => {
      const rec = (c.recommendation || "").toUpperCase().replace(/_/g, "");

      if (activeFilter === "SHORTLIST") return rec === "SHORTLIST" || rec === "STRONGSHORTLIST";
      if (activeFilter === "REVIEW") return rec === "REVIEW";
      if (activeFilter === "REJECT") return rec === "REJECT";

      return true;
    });
  }, [candidates, activeFilter]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* HEADER BLOCK */}
      <Box
        tabIndex={0}
        sx={{
          mb: 4,
          p: 0,
          bgcolor: "transparent",
          border: "none",
          borderRadius: 0,
          outline: "none",
          boxShadow: "inset 0 -1px 0 transparent",
          "&:focus-visible": {
            boxShadow: "inset 0 -2px 0 #1E3A8A",
          },
        }}
      >
        <Typography
          noWrap
          sx={{
            color: "#1E3A8A",
            fontSize: {
              xs: "1.6rem",
              sm: "1.8rem",
              md: "2rem",
              lg: "2.2rem",
            },
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: "-0.03em",
            mb: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {header}
        </Typography>

        <Stack direction="row" spacing={2} color="text.secondary" sx={{ flexWrap: "wrap" }}>
          {job?.company_name || job?.companyName ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <BusinessIcon fontSize="small" />
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 500,
                }}
              >{job.company_name || job.companyName}</Typography>
            </Stack>
          ) : null}

          {job?.location ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <LocationOnIcon fontSize="small" />
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 500,
                }}
              >{job.location}</Typography>
            </Stack>
          ) : null}
        </Stack>
      </Box>

      {/* CARDS BLOCK */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          mb: 4,
          width: "100%",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(190px, 1fr))",
          },
        }}
      >
        <SummaryCard
          title="Total Applicants"
          value={stats.total}
          subtext="All Time"
          icon={<PeopleAltIcon fontSize="small" />}
          color="primary.main"
          onClick={() => setActiveFilter("ALL")}
          selected={activeFilter === "ALL"}
        />

        <SummaryCard
          title="Shortlisted"
          value={stats.shortlisted.count}
          subtext={`${stats.shortlisted.pct}%`}
          icon={<CheckCircleIcon fontSize="small" />}
          color="success.main"
          onClick={() => setActiveFilter("SHORTLIST")}
          selected={activeFilter === "SHORTLIST"}
        />

        <SummaryCard
          title="Review"
          value={stats.review.count}
          subtext={`${stats.review.pct}%`}
          icon={<HourglassEmptyIcon fontSize="small" />}
          color="warning.main"
          onClick={() => setActiveFilter("REVIEW")}
          selected={activeFilter === "REVIEW"}
        />

        <SummaryCard
          title="Rejected"
          value={stats.rejected.count}
          subtext={`${stats.rejected.pct}%`}
          icon={<ErrorIcon fontSize="small" />}
          color="error.main"
          onClick={() => setActiveFilter("REJECT")}
          selected={activeFilter === "REJECT"}
        />
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <CandidateTable
        candidates={filteredCandidates}
        loading={loading}
        onViewResume={(row) => {
          if (row?.resume_url) {
            window.open(row.resume_url, "_blank");
          } else if (row) {
            viewResume(
              row.resume_key || row.resume_path || row.resume_url || row.resume,
              row.resume_original_name
            );
          }
        }}
      />
    </Container>
  );
}
